import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PencilSimple, Check, X, PaperPlaneTilt, CaretDown, Funnel, DownloadSimple } from "@phosphor-icons/react";
import axios from "axios";
import AdminRealisationsPanel from "./AdminRealisationsPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("altura_connect_token");
const propAx = axios.create({ baseURL: `${API}/api/property` });
propAx.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const SERVICE_LABELS = { auto: "Automobile", mobilier: "Mobilier", appartement: "Appartement", commercial: "Commercial" };
const STATUS_LABELS = { pending: "En attente", confirmed: "Confirmée", completed: "Effectuée", cancelled: "Annulée" };
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-green-200 text-green-900",
  cancelled: "bg-red-100 text-red-800",
};

const Badge = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${STATUS_COLORS[status] || "bg-gray-100"}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const AdminPropertyTab = ({ onReload }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tariffs, setTariffs] = useState(null);
  const [showTariffs, setShowTariffs] = useState(false);
  const [tariffSaving, setTariffSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [showReal, setShowReal] = useState(false);

  const loadRequests = async () => {
    try {
      const { data } = await propAx.get("/admin/requests");
      setRequests(data);
    } catch { /* silent */ }
  };

  const loadTariffs = async () => {
    try {
      const { data } = await propAx.get("/admin/tariffs");
      setTariffs(data);
    } catch { /* silent */ }
  };

  const loadReviews = async () => {
    try {
      const { data } = await propAx.get("/admin/reviews");
      setReviews(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadRequests(); loadTariffs(); loadReviews(); }, []);

  const filtered = requests.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (serviceFilter !== "all" && r.service_type !== serviceFilter) return false;
    return true;
  });

  const openEdit = (r) => {
    setEditing(r.id);
    setEditForm({
      modified_price: r.modified_price || r.total_amount || 0,
      modified_date: r.modified_date || r.desired_date || "",
      modified_time: r.modified_time || r.desired_time || "",
      admin_notes: r.admin_notes || "",
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await propAx.put(`/admin/requests/${editing}`, editForm);
      toast.success("Demande modifiée");
      setEditing(null);
      loadRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await propAx.put(`/admin/requests/${id}`, { status });
      toast.success(`Statut mis à jour: ${STATUS_LABELS[status]}`);
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const confirmAndNotify = async (id) => {
    try {
      const { data } = await propAx.post(`/admin/requests/${id}/confirm-send`);
      toast.success(data.email_sent ? "Confirmée — email envoyé au client" : "Confirmée — email non envoyé");
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const saveTariffs = async () => {
    if (!tariffs) return;
    setTariffSaving(true);
    try {
      await propAx.put("/admin/tariffs", tariffs);
      toast.success("Tarifs mis à jour");
    } catch (err) { toast.error("Erreur lors de la sauvegarde"); }
    finally { setTariffSaving(false); }
  };

  const approveReview = async (id) => {
    try { await propAx.put(`/admin/reviews/${id}/approve`); toast.success("Avis approuvé"); loadReviews(); } catch { toast.error("Erreur"); }
  };
  const rejectReview = async (id) => {
    try { await propAx.put(`/admin/reviews/${id}/reject`); toast.success("Avis masqué"); loadReviews(); } catch { toast.error("Erreur"); }
  };
  const deleteReview = async (id) => {
    try { await propAx.delete(`/admin/reviews/${id}`); toast.success("Avis supprimé"); loadReviews(); } catch { toast.error("Erreur"); }
  };

  const formatDate = (d) => {
    if (!d || !d.includes("-")) return d;
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-heading text-xl text-[#1A1A1A]">Property Services</h2>
        <div className="flex gap-2">
          <button onClick={() => { setShowReal(!showReal); setShowReviews(false); setShowTariffs(false); }}
            className={`px-3 py-1.5 text-xs border transition-colors ${showReal ? "bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]/50"}`}
            data-testid="toggle-realisations">
            Réalisations
          </button>
          <button onClick={() => { setShowReviews(!showReviews); setShowTariffs(false); setShowReal(false); }}
            className={`px-3 py-1.5 text-xs border transition-colors ${showReviews ? "bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]/50"}`}
            data-testid="toggle-reviews">
            Avis ({reviews.length})
          </button>
          <button onClick={() => { setShowTariffs(!showTariffs); setShowReviews(false); setShowReal(false); }}
            className={`px-3 py-1.5 text-xs border transition-colors ${showTariffs ? "bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]/50"}`}
            data-testid="toggle-tariffs">
            Tarifs
          </button>
        </div>
      </div>

      {showReal && <AdminRealisationsPanel propAx={propAx} />}

      {/* Tariff Configuration */}
      {showTariffs && tariffs && (
        <div className="bg-white border border-[#1A1A1A]/10 p-5 mb-6 space-y-4" data-testid="tariff-config">
          <h3 className="text-sm font-medium text-[#1A1A1A]">Configuration des tarifs</h3>

          {/* Auto prices */}
          <div>
            <p className="text-xs text-[#1A1A1A]/50 uppercase tracking-wide mb-2">Nettoyage Automobile (€)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[#1A1A1A]/50">
                  <th className="py-1 pr-2">Formule</th>
                  <th className="py-1 px-2">Citadine</th>
                  <th className="py-1 px-2">Berline</th>
                  <th className="py-1 px-2">SUV/Van</th>
                </tr></thead>
                <tbody>
                  {["interieur_eco", "interieur_premium", "exterieur", "complet"].map(f => (
                    <tr key={f} className="border-t border-[#1A1A1A]/5">
                      <td className="py-1.5 pr-2 text-[#1A1A1A]">{f.replace(/_/g, " ")}</td>
                      {["citadine", "berline", "suv"].map(cat => (
                        <td key={cat} className="py-1.5 px-2">
                          <input type="number" className="w-16 p-1 border border-[#1A1A1A]/20 text-center text-xs"
                            value={tariffs.auto_prices?.[cat]?.[f] || ""}
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              setTariffs(prev => ({
                                ...prev,
                                auto_prices: {
                                  ...prev.auto_prices,
                                  [cat]: { ...(prev.auto_prices?.[cat] || {}), [f]: val }
                                }
                              }));
                            }}
                            data-testid={`tariff-${cat}-${f}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Apartment rates */}
          <div>
            <p className="text-xs text-[#1A1A1A]/50 uppercase tracking-wide mb-2">Appartement (€/h)</p>
            <div className="flex gap-4">
              {tariffs.apartment_formulas && Object.entries(tariffs.apartment_formulas).map(([k, v]) => (
                <div key={k}>
                  <label className="text-xs text-[#1A1A1A]/60 block mb-1">{v.name || k}</label>
                  <input type="number" className="w-20 p-1.5 border border-[#1A1A1A]/20 text-xs text-center"
                    value={v.hourly_rate || ""}
                    onChange={e => {
                      setTariffs(prev => ({
                        ...prev,
                        apartment_formulas: {
                          ...prev.apartment_formulas,
                          [k]: { ...v, hourly_rate: parseFloat(e.target.value) || 0 }
                        }
                      }));
                    }}
                    data-testid={`tariff-apt-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Furniture minimum */}
          <div>
            <p className="text-xs text-[#1A1A1A]/50 uppercase tracking-wide mb-2">Mobilier — Minimum (€)</p>
            <input type="number" className="w-20 p-1.5 border border-[#1A1A1A]/20 text-xs text-center"
              value={tariffs.furniture_minimum || ""}
              onChange={e => setTariffs(prev => ({ ...prev, furniture_minimum: parseFloat(e.target.value) || 0 }))}
              data-testid="tariff-furniture-min"
            />
          </div>

          <button onClick={saveTariffs} disabled={tariffSaving}
            className="bg-[#1A1A1A] text-white px-4 py-2 text-xs uppercase tracking-wide hover:bg-[#333] disabled:opacity-50"
            data-testid="save-tariffs-btn">
            {tariffSaving ? "Sauvegarde..." : "Sauvegarder les tarifs"}
          </button>
        </div>
      )}

      {/* Reviews Management */}
      {showReviews && (
        <div className="bg-white border border-[#1A1A1A]/10 p-5 mb-6 space-y-3" data-testid="reviews-panel">
          <h3 className="text-sm font-medium text-[#1A1A1A]">Gestion des avis clients</h3>
          {reviews.length === 0 ? (
            <p className="text-[#1A1A1A]/50 text-xs">Aucun avis pour le moment.</p>
          ) : reviews.map(r => (
            <div key={r.id} className="flex items-start gap-3 p-3 border border-[#1A1A1A]/5 bg-[#EDEBE4]/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <span key={s} className={`text-xs ${r.rating >= s ? "text-[#D4A853]" : "text-[#1A1A1A]/15"}`}>&#9733;</span>)}</div>
                  <span className="text-[#1A1A1A] text-xs font-medium">{r.client_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium" style={{ background: r.approved ? "#dcfce7" : "#fef3c7", color: r.approved ? "#166534" : "#92400e" }}>
                    {r.approved ? "Publié" : "En attente"}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${
                    r.service_type === "auto" ? "bg-blue-100 text-blue-700" :
                    r.service_type === "mobilier" ? "bg-purple-100 text-purple-700" :
                    r.service_type === "appartement" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"
                  }`}>{SERVICE_LABELS[r.service_type] || r.service_type}</span>
                </div>
                {r.comment && <p className="text-[#1A1A1A]/70 text-xs">{r.comment}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                {!r.approved && (
                  <button onClick={() => approveReview(r.id)} className="px-2 py-1 text-[10px] bg-green-700 text-white hover:bg-green-800" data-testid={`approve-${r.id}`}>Publier</button>
                )}
                {r.approved && (
                  <button onClick={() => rejectReview(r.id)} className="px-2 py-1 text-[10px] border border-[#1A1A1A]/20 text-[#1A1A1A]/60 hover:border-[#1A1A1A]/40" data-testid={`reject-${r.id}`}>Masquer</button>
                )}
                <button onClick={() => deleteReview(r.id)} className="px-2 py-1 text-[10px] border border-red-300 text-red-600 hover:bg-red-50" data-testid={`delete-${r.id}`}>Suppr.</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1 text-xs text-[#1A1A1A]/50"><Funnel size={12} /></div>
        {[
          { id: "all", label: "Toutes" },
          { id: "pending", label: "En attente" },
          { id: "confirmed", label: "Confirmées" },
          { id: "completed", label: "Effectuées" },
          { id: "cancelled", label: "Annulées" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1 text-xs border transition-colors ${filter === f.id ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/60"}`}
            data-testid={`filter-${f.id}`}
          >{f.label}</button>
        ))}
        <span className="text-[#1A1A1A]/30 mx-1">|</span>
        {[
          { id: "all", label: "Tous services" },
          { id: "auto", label: "Auto" },
          { id: "mobilier", label: "Mobilier" },
          { id: "appartement", label: "Appart." },
          { id: "commercial", label: "Locaux" },
        ].map(f => (
          <button key={f.id} onClick={() => setServiceFilter(f.id)}
            className={`px-3 py-1 text-xs border transition-colors ${serviceFilter === f.id ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/60"}`}
            data-testid={`svc-filter-${f.id}`}
          >{f.label}</button>
        ))}
      </div>

      <p className="text-xs text-[#1A1A1A]/40 mb-3">{filtered.length} demande(s)</p>

      {/* Request list */}
      <div className="space-y-3">
        {filtered.map(r => {
          const ref = r.id?.slice(0, 8).toUpperCase();
          const isExpanded = expandedId === r.id;
          const isEditing = editing === r.id;
          return (
            <div key={r.id} className="bg-white border border-[#1A1A1A]/10" data-testid={`prop-req-${ref}`}>
              {/* Header */}
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : r.id)}>
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">
                    PRO-{ref}
                    <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded-sm ${
                      r.service_type === "auto" ? "bg-blue-100 text-blue-700" :
                      r.service_type === "mobilier" ? "bg-purple-100 text-purple-700" :
                      r.service_type === "appartement" ? "bg-amber-100 text-amber-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{SERVICE_LABELS[r.service_type] || r.service_type}</span>
                  </p>
                  <p className="text-xs text-[#1A1A1A]/50">
                    {r.client_name} {r.client_surname} — {formatDate(r.desired_date || r.modified_date)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge status={r.status} />
                  <p className="font-heading text-lg text-[#1A1A1A]">
                    {r.sur_devis && r.total_amount === 0 ? "Devis" : `${r.modified_price || r.total_amount || 0}€`}
                  </p>
                  <CaretDown size={14} className={`text-[#1A1A1A]/40 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-[#1A1A1A]/5 p-4 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-[#1A1A1A]/50 uppercase tracking-wide mb-0.5">Client</p>
                      <p className="text-[#1A1A1A]">{r.client_name} {r.client_surname}</p>
                      <p className="text-[#1A1A1A]/60">{r.client_phone} — {r.client_email}</p>
                    </div>
                    <div>
                      <p className="text-[#1A1A1A]/50 uppercase tracking-wide mb-0.5">Adresse</p>
                      <p className="text-[#1A1A1A]">{r.address}</p>
                      {r.address_details && <p className="text-[#1A1A1A]/60">{r.address_details}</p>}
                    </div>
                  </div>

                  {/* Service details */}
                  {r.breakdown && r.breakdown.length > 0 && (
                    <div>
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Détail</p>
                      {r.breakdown.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs py-0.5">
                          <span className="text-[#1A1A1A]">{b.label}</span>
                          <span className="text-[#1A1A1A] font-medium">{b.amount > 0 ? `${b.amount}€` : "Devis"}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {r.comments && (
                    <div>
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-0.5">Commentaires client</p>
                      <p className="text-[#1A1A1A]/70 text-xs">{r.comments}</p>
                    </div>
                  )}

                  {/* Photo Gallery */}
                  {r.photo_urls && r.photo_urls.length > 0 && (
                    <div data-testid="photo-gallery">
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-2">Photos client ({r.photo_urls.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {r.photo_urls.map((url, i) => (
                          <a key={i} href={`${API}${url}`} target="_blank" rel="noopener noreferrer"
                            className="block w-20 h-20 border border-[#1A1A1A]/10 overflow-hidden hover:border-[#1A1A1A]/40 transition-colors">
                            <img src={`${API}${url}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {r.admin_notes && (
                    <div>
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-0.5">Notes admin</p>
                      <p className="text-[#1A1A1A]/70 text-xs">{r.admin_notes}</p>
                    </div>
                  )}

                  {/* Edit form */}
                  {isEditing ? (
                    <div className="bg-[#EDEBE4] p-4 space-y-3" data-testid="edit-form">
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[#1A1A1A]/60 text-xs block mb-1">Prix (€)</label>
                          <input type="number" value={editForm.modified_price} onChange={e => setEditForm(p => ({ ...p, modified_price: parseFloat(e.target.value) || 0 }))}
                            className="w-full p-2 text-sm border border-[#1A1A1A]/20 bg-white" data-testid="edit-price" />
                        </div>
                        <div>
                          <label className="text-[#1A1A1A]/60 text-xs block mb-1">Date</label>
                          <input type="date" value={editForm.modified_date} onChange={e => setEditForm(p => ({ ...p, modified_date: e.target.value }))}
                            className="w-full p-2 text-sm border border-[#1A1A1A]/20 bg-white" />
                        </div>
                        <div>
                          <label className="text-[#1A1A1A]/60 text-xs block mb-1">Heure</label>
                          <input type="time" value={editForm.modified_time} onChange={e => setEditForm(p => ({ ...p, modified_time: e.target.value }))}
                            className="w-full p-2 text-sm border border-[#1A1A1A]/20 bg-white" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[#1A1A1A]/60 text-xs block mb-1">Notes admin</label>
                        <textarea value={editForm.admin_notes} onChange={e => setEditForm(p => ({ ...p, admin_notes: e.target.value }))} rows={2}
                          className="w-full p-2 text-sm border border-[#1A1A1A]/20 bg-white resize-none" data-testid="edit-notes" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} disabled={saving} className="bg-[#1A1A1A] text-white px-4 py-1.5 text-xs flex items-center gap-1" data-testid="save-edit">
                          <Check size={12} /> {saving ? "..." : "Enregistrer"}
                        </button>
                        <button onClick={() => setEditing(null)} className="border border-[#1A1A1A]/20 px-4 py-1.5 text-xs flex items-center gap-1">
                          <X size={12} /> Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Actions */
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1A1A1A]/5">
                      <button onClick={() => openEdit(r)} className="px-3 py-1.5 text-xs border border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]/50 flex items-center gap-1" data-testid="btn-edit">
                        <PencilSimple size={12} /> Modifier
                      </button>
                      <a href={`${API}/api/property/admin/requests/${r.id}/pdf`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          propAx.get(`/admin/requests/${r.id}/pdf`, { responseType: 'blob' }).then(res => {
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a'); a.href = url; a.download = `ALTURA_PRO-${r.id?.slice(0,8).toUpperCase()}.pdf`; a.click();
                          }).catch(() => toast.error("Erreur PDF"));
                        }}
                        className="px-3 py-1.5 text-xs border border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]/50 flex items-center gap-1 cursor-pointer" data-testid="btn-pdf">
                        <DownloadSimple size={12} /> PDF
                      </a>
                      {r.status === "pending" && (
                        <button onClick={() => confirmAndNotify(r.id)} className="px-3 py-1.5 text-xs bg-green-700 text-white hover:bg-green-800 flex items-center gap-1" data-testid="btn-confirm">
                          <PaperPlaneTilt size={12} /> Confirmer & envoyer
                        </button>
                      )}
                      {(r.status === "pending" || r.status === "confirmed") && (
                        <button onClick={() => updateStatus(r.id, "completed")} className="px-3 py-1.5 text-xs border border-green-600 text-green-700 hover:bg-green-50 flex items-center gap-1" data-testid="btn-complete">
                          <Check size={12} /> Effectuée
                        </button>
                      )}
                      {r.status !== "cancelled" && r.status !== "completed" && (
                        <button onClick={() => updateStatus(r.id, "cancelled")} className="px-3 py-1.5 text-xs border border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-1" data-testid="btn-cancel">
                          <X size={12} /> Annuler
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-[#1A1A1A]/50 text-sm py-4">Aucune demande Property Services</p>}
      </div>
    </div>
  );
};

export default AdminPropertyTab;
